import InfoTip from '../../components/InfoTip';
import React, { useState } from 'react';
import { formatGermanTime } from '../../helpers/germanTime';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Link,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { WebinarOrderMappingData } from '../../helpers/webinarStatisticsData';

interface OrderMappingsTabProps {
  data: WebinarOrderMappingData[];
  loading: boolean;
  colors: any;
  onMappingMethodChange: (method: string) => void;
  selectedMappingMethod: string;
}

const OrderMappingsTab: React.FC<OrderMappingsTabProps> = ({
  data,
  loading,
  colors,
  onMappingMethodChange,
  selectedMappingMethod,
}) => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<WebinarOrderMappingData | null>(null);

  // Calculate summary stats
  const totalOrders = data.length;
  const mappedOrders = data.filter((m) => m.webinar_id !== null).length;
  const unmappedOrders = data.filter((m) => m.mapping_method === 'unmapped').length;

  // Handle row click
  const handleRowClick = (row: WebinarOrderMappingData) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  return (
    <Box>
      {/* Filter Controls */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel sx={{ color: colors.grey[100] }}>Mapping-Methode Filter</InputLabel>
          <Select
            value={selectedMappingMethod}
            onChange={(e) => onMappingMethodChange(e.target.value)}
            label="Mapping-Methode Filter"
            sx={{
              color: colors.grey[100],
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: colors.grey[300],
              },
            }}
          >
            <MenuItem value="all">Alle anzeigen</MenuItem>
            <MenuItem value="rid">RID Match</MenuItem>
            <MenuItem value="banner_webinar_id">Banner Webinar-ID</MenuItem>
            <MenuItem value="email_banner">Email + Banner Date</MenuItem>
            <MenuItem value="email_doi_url">Email + DOI URL</MenuItem>
            <MenuItem value="email_affiliate">Email + Affiliate</MenuItem>
            <MenuItem value="unmapped">Nicht Gemapped</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Summary Cards - 3 columns */}
      <Box
        sx={{
          display: 'grid',
          // Am Handy untereinander — drei feste Spalten schnitten die Kacheln bei 390 px ab (Bedienprüfung 28.10.).
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: '20px',
          mb: '20px',
        }}
      >
        {/* Total Orders */}
        <Box
          sx={{
            backgroundColor: colors.primary[400],
            p: '20px',
            borderRadius: '8px',
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
            {totalOrders.toLocaleString()}
          </Typography>
          <Typography color={colors.greenAccent[500]}>
            Gesamt-Orders
            <InfoTip text="Alle Webinar-Bestellungen von Finanztrends-Kunden im Zeitraum. Achtung: dieser Tab filtert nach BESTELLDATUM, die anderen Tabs nach Webinar-Datum." />
          </Typography>
        </Box>

        {/* Mapped Orders */}
        <Box
          sx={{
            backgroundColor: colors.primary[400],
            p: '20px',
            borderRadius: '8px',
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
            {mappedOrders.toLocaleString()}
          </Typography>
          <Typography color={colors.greenAccent[500]}>
            Gemappte Orders (
            {totalOrders > 0 ? ((mappedOrders / totalOrders) * 100).toFixed(1) : '0.0'}
            %)
            <InfoTip text="Bestellungen, die eindeutig einem Webinar zugeordnet sind. Nur diese erscheinen in den Auswertungen." />
          </Typography>
        </Box>

        {/* Unmapped Orders */}
        <Box
          sx={{
            backgroundColor: colors.primary[400],
            p: '20px',
            borderRadius: '8px',
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100] }}>
            {unmappedOrders.toLocaleString()}
          </Typography>
          <Typography color={colors.redAccent[500]}>
            Nicht Gemapped (
            {totalOrders > 0 ? ((unmappedOrders / totalOrders) * 100).toFixed(1) : '0.0'}
            %)
            <InfoTip text="Webinar-Bestellungen, für die kein Webinar gefunden wurde. Sie fehlen in allen Webinar-Auswertungen." />
          </Typography>
        </Box>
      </Box>

      {/* Mapping Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: 'black',
                  backgroundColor: colors.primary[500],
                  fontWeight: 'bold',
                }}
              >
                Bestellnummer
              </TableCell>
              <TableCell
                sx={{
                  color: 'black',
                  backgroundColor: colors.primary[500],
                  fontWeight: 'bold',
                }}
              >
                E-Mail
              </TableCell>
              <TableCell
                sx={{
                  color: 'black',
                  backgroundColor: colors.primary[500],
                  fontWeight: 'bold',
                }}
              >
                Webinar
              </TableCell>
              <TableCell
                sx={{
                  color: 'black',
                  backgroundColor: colors.primary[500],
                  fontWeight: 'bold',
                }}
              >
                Mapping-Methode
                <InfoTip text="Wie die Bestellung dem Webinar zugeordnet wurde: RID (persönliche Kennung im Bestelllink), Banner (Webinar-Kennung im Link), E-Mail + Banner/DOI/Affiliate, Affiliate-Konzept (nur Produktkonzept, ohne Anmeldung)." />
              </TableCell>
              <TableCell
                sx={{
                  color: 'black',
                  backgroundColor: colors.primary[500],
                  fontWeight: 'bold',
                }}
              >
                Bestell-Datum
                <InfoTip text="Datum der Bestellung — Grundlage für den Zeitraum-Filter dieses Tabs." />
              </TableCell>
              <TableCell
                sx={{
                  color: 'black',
                  backgroundColor: colors.primary[500],
                  fontWeight: 'bold',
                }}
              >
                Offer URL
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: colors.grey[300], textAlign: 'center' }}>
                  Lade Daten...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: colors.grey[300], textAlign: 'center' }}>
                  Keine Daten verfügbar
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={`${row.ordernumber}-${index}`}
                  onClick={() => handleRowClick(row)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: colors.primary[300] },
                  }}
                >
                  <TableCell sx={{ color: 'black' }}>{row.ordernumber}</TableCell>
                  <TableCell sx={{ color: 'black' }}>{row.email}</TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    {row.webinar_title || (
                      <em style={{ color: colors.grey[400] }}>Kein Webinar gemapped</em>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <Chip
                      label={row.mapping_method.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor:
                          row.mapping_method === 'rid'
                            ? colors.greenAccent[600]
                            : row.mapping_method === 'banner_webinar_id'
                              ? colors.greenAccent[500]
                              : row.mapping_method === 'email_banner'
                                ? colors.greenAccent[700]
                                : row.mapping_method === 'email_doi_url'
                                  ? colors.greenAccent[800]
                                  : row.mapping_method === 'email_affiliate'
                                    ? colors.blueAccent[600]
                                    : row.mapping_method === 'affiliate_concept'
                                      ? '#8e24aa'
                                      : colors.redAccent[600],
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    {formatGermanTime(row.created_on, 'D.M.YYYY')}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'black',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.offerurl ? (
                      <Link
                        href={row.offerurl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: colors.blueAccent[400],
                          wordBreak: 'break-all',
                        }}
                      >
                        {row.offerurl.length > 50
                          ? row.offerurl.substring(0, 50) + '...'
                          : row.offerurl}
                      </Link>
                    ) : (
                      <em style={{ color: colors.grey[400] }}>Keine URL</em>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mapping Distribution Summary */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2 }}>
          Mapping-Methoden Verteilung
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '10px',
          }}
        >
          {[
            'rid',
            'banner_webinar_id',
            'email_banner',
            'email_doi_url',
            'email_affiliate',
            'affiliate_concept',
            'unmapped',
          ].map((method) => {
            const count = data.filter((m) => m.mapping_method === method).length;
            const percentage = totalOrders > 0 ? ((count / totalOrders) * 100).toFixed(1) : '0.0';
            return (
              <Paper key={method} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
                <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
                  {method === 'rid'
                    ? 'RID'
                    : method === 'banner_webinar_id'
                      ? 'BANNER-ID'
                      : method === 'email_banner'
                        ? 'EMAIL+BANNER'
                        : method === 'email_doi_url'
                          ? 'EMAIL+DOI_URL'
                          : method === 'email_affiliate'
                            ? 'EMAIL+AFFILIATE'
                            : method === 'affiliate_concept'
                              ? 'AFFILIATE-KONZEPT'
                              : 'UNMAPPED'}
                </Typography>
                <Typography variant="h4" sx={{ color: colors.greenAccent[500] }}>
                  {count}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                  {percentage}%
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: 'white' },
        }}
      >
        <DialogTitle
          sx={{
            color: 'black',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          Webinar Mapping Details
          <IconButton onClick={() => setModalOpen(false)} sx={{ color: 'black' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedRow && (
            <Box>
              {/* Order Information */}
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="h6" sx={{ color: '#333', mb: 2, fontWeight: 'bold' }}>
                  Bestell-Information
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '200px 1fr',
                    gap: 1,
                  }}
                >
                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>Bestellnummer:</Typography>
                  <Typography sx={{ color: '#000' }}>{selectedRow.ordernumber}</Typography>

                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>E-Mail:</Typography>
                  <Typography sx={{ color: '#000' }}>{selectedRow.email}</Typography>

                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>Bestell-Datum:</Typography>
                  <Typography sx={{ color: '#000' }}>
                    {formatGermanTime(selectedRow.created_on, 'D.M.YYYY')}
                  </Typography>

                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>Banner:</Typography>
                  <Typography sx={{ color: '#000', wordBreak: 'break-all' }}>
                    {selectedRow.banner || <em style={{ color: '#999' }}>Kein Banner</em>}
                  </Typography>

                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>Offer URL:</Typography>
                  {selectedRow.offerurl ? (
                    <Link
                      href={selectedRow.offerurl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: '#1976d2', wordBreak: 'break-all' }}
                    >
                      {selectedRow.offerurl}
                    </Link>
                  ) : (
                    <Typography sx={{ color: '#999' }}>
                      <em>Keine URL</em>
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* Mapping Information */}
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="h6" sx={{ color: '#333', mb: 2, fontWeight: 'bold' }}>
                  Mapping-Information
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '200px 1fr',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>
                    Mapping-Methode:
                  </Typography>
                  <Box>
                    <Chip
                      label={selectedRow.mapping_method.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor:
                          selectedRow.mapping_method === 'rid'
                            ? '#4caf50'
                            : selectedRow.mapping_method === 'banner_webinar_id'
                              ? '#43a047'
                              : selectedRow.mapping_method === 'email_banner'
                                ? '#66bb6a'
                                : selectedRow.mapping_method === 'email_doi_url'
                                  ? '#388e3c'
                                  : selectedRow.mapping_method === 'email_affiliate'
                                    ? '#2196f3'
                                    : '#f44336',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>

                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>Webinar:</Typography>
                  <Typography sx={{ color: '#000' }}>
                    {selectedRow.webinar_title || (
                      <em style={{ color: '#999' }}>Kein Webinar gemapped</em>
                    )}
                  </Typography>

                  {selectedRow.webinar_start_date && (
                    <>
                      <Typography sx={{ color: '#666', fontWeight: 'bold' }}>
                        Webinar-Datum:
                      </Typography>
                      <Typography sx={{ color: '#000' }}>
                        {formatGermanTime(selectedRow.webinar_start_date, 'D.M.YYYY')}
                      </Typography>
                    </>
                  )}

                  {selectedRow.affiliate && (
                    <>
                      <Typography sx={{ color: '#666', fontWeight: 'bold' }}>Affiliate:</Typography>
                      <Typography sx={{ color: '#000' }}>{selectedRow.affiliate}</Typography>
                    </>
                  )}

                  {selectedRow.adref && (
                    <>
                      <Typography sx={{ color: '#666', fontWeight: 'bold' }}>AdRef:</Typography>
                      <Typography sx={{ color: '#000' }}>{selectedRow.adref}</Typography>
                    </>
                  )}

                  {selectedRow.registration_email && (
                    <>
                      <Typography sx={{ color: '#666', fontWeight: 'bold' }}>
                        Registrierungs-E-Mail:
                      </Typography>
                      <Typography sx={{ color: '#000' }}>
                        {selectedRow.registration_email}
                      </Typography>
                    </>
                  )}

                  <Typography sx={{ color: '#666', fontWeight: 'bold' }}>
                    Zuletzt aktualisiert:
                  </Typography>
                  <Typography sx={{ color: '#000' }}>
                    {/* TIMESTAMP-Spalte: API liefert echtes UTC — plain Date ist hier KORREKT (tz-ok) */}
                    {new Date(selectedRow.mapping_updated_at).toLocaleString('de-DE')}
                  </Typography>
                </Box>
              </Paper>

              {/* Mapping Logic Explanation */}
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="h6" sx={{ color: '#333', mb: 2, fontWeight: 'bold' }}>
                  {selectedRow.mapping_method === 'unmapped'
                    ? 'Warum nicht gemapped?'
                    : 'Wie wurde gemapped?'}
                </Typography>

                {selectedRow.mapping_method === 'rid' && (
                  <Box>
                    <Typography sx={{ color: '#4caf50', fontWeight: 'bold', mb: 1 }}>
                      ✓ RID Match (Höchste Genauigkeit)
                    </Typography>
                    <Typography sx={{ color: '#000', mb: 1 }}>
                      Die Anfo wurde über den <strong>utm_term Parameter</strong> in der Offer URL
                      zugeordnet.
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#e8f5e9',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #c8e6c9',
                      }}
                    >
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>RID:</strong> {selectedRow.rid}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: '#555', fontSize: '0.9em' }}>
                      Diese Methode ist die genaueste, da jeder Registrierungs-Link eine eindeutige
                      ID (RID) enthält, die direkt zur Webinar-Registrierung zurückführt.
                    </Typography>
                  </Box>
                )}

                {selectedRow.mapping_method === 'banner_webinar_id' && (
                  <Box>
                    <Typography sx={{ color: '#43a047', fontWeight: 'bold', mb: 1 }}>
                      ✓ Banner Webinar-ID Match (Sehr genau)
                    </Typography>
                    <Typography sx={{ color: '#000', mb: 1 }}>
                      Die Anfo wurde über die <strong>Webinar-ID im Banner-Feld</strong> zugeordnet.
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#e8f5e9',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #c8e6c9',
                      }}
                    >
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>Banner:</strong> {selectedRow.banner}
                      </Typography>
                      {selectedRow.rid && (
                        <Typography sx={{ color: '#2e7d32', fontSize: '0.9em', mt: 0.5 }}>
                          <strong>RID:</strong> {selectedRow.rid}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ color: '#555', fontSize: '0.9em' }}>
                      Das Banner-Feld enthält eine direkte Webinar-ID, die mit einer bestehenden
                      Webinar-Registrierung übereinstimmt. Diese Methode ist die zweitgenaueste nach
                      RID.
                    </Typography>
                    {selectedRow.rid && (
                      <Typography sx={{ color: '#555', fontSize: '0.9em', mt: 1 }}>
                        Zusätzlich trägt das Banner die <strong>RID der Anmeldung</strong> — gesetzt
                        von der Nachfass-Mail. Die Zuordnung ist damit nicht nur auf das Webinar,
                        sondern auf die einzelne Registrierung genau.
                      </Typography>
                    )}
                  </Box>
                )}

                {selectedRow.mapping_method === 'email_banner' && (
                  <Box>
                    <Typography sx={{ color: '#4caf50', fontWeight: 'bold', mb: 1 }}>
                      ✓ Email + Banner Date Match (Sehr genau)
                    </Typography>
                    <Typography sx={{ color: '#000', mb: 1 }}>
                      Die Anfo wurde über eine Kombination aus <strong>E-Mail-Adresse</strong> und{' '}
                      <strong>Banner-Datum</strong> zugeordnet.
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#e8f5e9',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #c8e6c9',
                      }}
                    >
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>E-Mail:</strong> {selectedRow.registered_email}
                      </Typography>
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>Banner-Datum:</strong>{' '}
                        {selectedRow.banner_date
                          ? new Date(selectedRow.banner_date).toLocaleDateString('de-DE')
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: '#555', fontSize: '0.9em' }}>
                      Diese E-Mail-Adresse ist für das Webinar registriert UND das Banner-Datum
                      stimmt mit dem Webinar-Startdatum überein. Dies löst das Problem von
                      Multi-Registranten (Kunden, die sich für mehrere Webinare angemeldet haben).
                    </Typography>
                  </Box>
                )}

                {selectedRow.mapping_method === 'email_doi_url' && (
                  <Box>
                    <Typography sx={{ color: '#388e3c', fontWeight: 'bold', mb: 1 }}>
                      ✓ Email+DOI URL Match (DOI Page Order)
                    </Typography>
                    <Typography sx={{ color: '#000', mb: 1 }}>
                      Die Anfo wurde über <strong>E-Mail + DOI URL</strong> zugeordnet.
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#e8f5e9',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #a5d6a7',
                      }}
                    >
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>Registrierte E-Mail:</strong> {selectedRow.registered_email}
                      </Typography>
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>Offer URL (Base):</strong>{' '}
                        {selectedRow.offerurl ? selectedRow.offerurl.split('?')[0] : 'N/A'}
                      </Typography>
                      <Typography sx={{ color: '#2e7d32', fontSize: '0.9em' }}>
                        <strong>DOI URL (Registrierung):</strong> Stimmt mit Offer URL überein
                      </Typography>
                    </Box>
                    <Typography sx={{ color: '#555', fontSize: '0.9em' }}>
                      Diese E-Mail-Adresse ist für das Webinar registriert UND die Offer URL stimmt
                      mit der DOI-Bestätigungsseiten-URL der Registrierung überein. Dies bestätigt,
                      dass die Anfo direkt auf der DOI-Bestätigungsseite aufgegeben wurde (häufig
                      mit Banner-Suffix &quot;_REG&quot;).
                    </Typography>
                  </Box>
                )}

                {selectedRow.mapping_method === 'email_affiliate' && (
                  <Box>
                    <Typography sx={{ color: '#2196f3', fontWeight: 'bold', mb: 1 }}>
                      ✓ Email+Affiliate Match (Fallback)
                    </Typography>
                    <Typography sx={{ color: '#000', mb: 1 }}>
                      Die Anfo wurde über <strong>E-Mail + Affiliate</strong> zugeordnet.
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#e3f2fd',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid #bbdefb',
                      }}
                    >
                      <Typography sx={{ color: '#1565c0', fontSize: '0.9em' }}>
                        <strong>Registrierte E-Mail:</strong> {selectedRow.registered_email}
                      </Typography>
                      <Typography sx={{ color: '#1565c0', fontSize: '0.9em' }}>
                        <strong>Banner:</strong> {selectedRow.banner}
                      </Typography>
                      <Typography sx={{ color: '#1565c0', fontSize: '0.9em' }}>
                        <strong>Affiliate (Registrierung):</strong> Stimmt mit Banner überein
                      </Typography>
                    </Box>
                    <Typography sx={{ color: '#555', fontSize: '0.9em' }}>
                      Diese E-Mail-Adresse ist für das Webinar registriert UND das Banner-Feld
                      stimmt mit dem Affiliate-Code der Registrierung überein. Dies ist eine
                      zuverlässige Fallback-Methode, wenn kein Banner-Datum extrahierbar ist.
                    </Typography>
                  </Box>
                )}

                {selectedRow.mapping_method === 'unmapped' && (
                  <Box>
                    <Typography sx={{ color: '#f44336', fontWeight: 'bold', mb: 1 }}>
                      ✗ Nicht Gemapped
                    </Typography>
                    <Typography sx={{ color: '#000', mb: 2 }}>
                      Diese Anfo konnte keinem Webinar zugeordnet werden.
                    </Typography>

                    <Typography sx={{ color: '#555', fontSize: '0.9em', mb: 1 }}>
                      <strong>Überprüfte Methoden:</strong>
                    </Typography>
                    <Box component="ul" sx={{ color: '#555', fontSize: '0.9em', pl: 3, mb: 2 }}>
                      <li>
                        <strong>RID Match:</strong>{' '}
                        {selectedRow.rid
                          ? `RID "${selectedRow.rid}" nicht in Webinar-Registrierungen gefunden`
                          : 'Kein utm_term Parameter in der Offer URL'}
                      </li>
                      <li>
                        <strong>Email+Banner Match:</strong>{' '}
                        {selectedRow.banner_date
                          ? `E-Mail nicht registriert ODER Banner-Datum (${new Date(selectedRow.banner_date).toLocaleDateString('de-DE')}) stimmt nicht überein`
                          : 'Kein Datum im Banner-Feld extrahierbar'}
                      </li>
                      <li>
                        <strong>Email+DOI URL Match:</strong> E-Mail nicht registriert ODER Offer
                        URL Base &quot;
                        {selectedRow.offerurl?.split('?')[0] || 'N/A'}&quot; stimmt nicht mit DOI
                        URL überein
                      </li>
                      <li>
                        <strong>Email+Affiliate Match:</strong> E-Mail nicht registriert ODER
                        Banner-Feld &quot;{selectedRow.banner}
                        &quot; stimmt nicht mit Affiliate-Code überein
                      </li>
                    </Box>

                    <Paper
                      sx={{
                        backgroundColor: '#ffebee',
                        p: 1.5,
                        border: '1px solid #ef9a9a',
                      }}
                    >
                      <Typography sx={{ color: '#c62828', fontSize: '0.9em' }}>
                        <strong>Manuelle Überprüfung erforderlich:</strong>
                        <br />
                        Diese Anfo sollte manuell überprüft werden, um festzustellen, ob sie
                        tatsächlich mit einem Webinar zusammenhängt oder ob die Tracking-Daten
                        unvollständig sind.
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OrderMappingsTab;
